import React, { useState, useEffect, useMemo } from 'react';
import { Activity, Users, TrendingUp, TrendingDown, Minus, FileText, Pencil } from 'lucide-react';
import { getPlayers, getGPS, getRPE, updateSessionNotes, resolveTargetsBatch, getAvailability } from '../api';
import EstimateModal from './gps/EstimateModal';
import { EstimatedValue, EstimatedMarker, parseBasis } from '../components/EstimatedMarker';
import { useSeason, dateToSeason } from '../SeasonContext';
import { rpeValueColor, rpeAssessment } from './WellnessInput';
import { useUrlParam } from '../hooks/useUrlParam';

const GAUGE_METRICS = [
  { key: 'TD',   label: 'Total Distance',      unit: 'm',  category: 'Endurance'     },
  { key: 'RD',   label: 'Run Dist. >14.4km/h', unit: 'm',  category: 'Endurance'     },
  { key: 'HID',  label: 'HID >19.8km/h',       unit: 'm',  category: 'High velocity' },
  { key: 'SD',   label: 'Sprint >25.2km/h',     unit: 'm',  category: 'High velocity' },
  { key: 'SA',   label: 'Short Actions',        unit: '',   category: 'Small spaces'  },
  { key: 'Expl', label: 'Explosive Dist.',      unit: 'm',  category: 'Small spaces'  },
];

export const STATUS_COLORS = { above: '#7c3aed', green: '#10b981', amber: '#f59e0b', below: '#ef4444', none: 'var(--text-dimmed)' };

export function getStatus(val, targets) {
  if (!targets || (targets.upper === 0 && targets.lower === 0)) return 'none';
  if (val > targets.upper)        return 'above';
  if (val >= targets.lower)       return 'green';
  if (val >= targets.lower * 0.9) return 'amber';
  return 'below';
}

export function getPct(val, targets) {
  if (!targets || targets.upper === 0) return null;
  if (val >= targets.lower && val <= targets.upper) return 100;
  if (val > targets.upper) return Math.round((val / targets.upper) * 100);
  if (targets.lower > 0) return Math.round((val / targets.lower) * 100);
  return 0;
}

function Gauge({ label, value, targets, unit, category }) {
  const status = getStatus(value, targets);
  const color  = STATUS_COLORS[status];
  const pct    = getPct(value, targets);
  const R = 52; const cx = 70; const cy = 70;
  const startDeg = -200; const sweepDeg = 220;
  const arcPath = (pctFill) => {
    const clamp = Math.min(Math.max(pctFill, 0), 1);
    const endDeg = startDeg + sweepDeg * clamp;
    const toRad = d => (d * Math.PI) / 180;
    const x1 = cx + R * Math.cos(toRad(startDeg)); const y1 = cy + R * Math.sin(toRad(startDeg));
    const x2 = cx + R * Math.cos(toRad(endDeg));   const y2 = cy + R * Math.sin(toRad(endDeg));
    const large = sweepDeg * clamp > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2}`;
  };
  const fillFraction = targets && targets.upper > 0 ? Math.min(value / targets.upper, 1.15) : 0;
  const lowerFrac = targets && targets.upper > 0 ? targets.lower / targets.upper : null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 120 }}>
      <div style={{ position: 'relative', width: 140, height: 100 }}>
        <svg width="140" height="105" viewBox="0 0 140 105" style={{ overflow: 'visible' }}>
          <path d={arcPath(1)} fill="none" stroke="var(--gauge-track)" strokeWidth="9" strokeLinecap="round" />
          {lowerFrac && <path d={arcPath(lowerFrac)} fill="none" stroke={color === STATUS_COLORS.none ? 'var(--gauge-track)' : color} strokeWidth="9" strokeLinecap="round" strokeOpacity="0.25" />}
          <path d={arcPath(Math.min(fillFraction, 1))} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" />
          {fillFraction > 1 && <path d={arcPath(fillFraction)} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeOpacity="0.4" />}
          <text x="70" y="68" textAnchor="middle" fill={color} fontSize="17" fontWeight="700" fontFamily="Inter, sans-serif">
            {value ? Math.round(value).toLocaleString() : '—'}
          </text>
          {pct !== null && (
            <text x="70" y="83" textAnchor="middle" fill="var(--text-muted)" fontSize="10" fontFamily="Inter, sans-serif">{pct}%</text>
          )}
        </svg>
      </div>
      <div style={{ textAlign: 'center', marginTop: -4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{label}</div>
        {targets && targets.upper > 0 && (
          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', marginTop: 1 }}>
            target: {Math.round(targets.lower).toLocaleString()}–{Math.round(targets.upper).toLocaleString()}
          </div>
        )}
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, background: 'var(--border)', borderRadius: 4, padding: '1px 6px', display: 'inline-block' }}>{category}</div>
      </div>
    </div>
  );
}

function LoadChart({ enriched, selfPlayerId = null }) {
  const players = enriched.flatMap(p => {
    const tgt = p.targets?.HMLD;
    const value = p.metrics.HMLD ?? 0;
    if (!tgt || (tgt.upper === 0 && tgt.lower === 0)) return [];
    const isMe = !selfPlayerId || String(p.player_id) === selfPlayerId;
    if (value >= tgt.lower && value <= tgt.upper) return [];
    if (value < tgt.lower) {
      const pct = tgt.lower > 0 ? Math.round(((value - tgt.lower) / tgt.lower) * 100) : 0;
      return [{ name: p.name.split(' ')[0], pct, status: value >= tgt.lower * 0.9 ? 'amber' : 'below', blur: !isMe }];
    }
    if (value > tgt.upper) {
      const pct = tgt.upper > 0 ? Math.round(((value - tgt.upper) / tgt.upper) * 100) : 0;
      return [{ name: p.name.split(' ')[0], pct, status: 'above', blur: !isMe }];
    }
    return [];
  }).sort((a, b) => b.pct - a.pct);
  if (!players.length) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--text-dimmed)', fontSize: 13 }}>No target data available</div>
  );
  const maxAbs = Math.max(...players.map(p => Math.abs(p.pct)), 30);
  const nameW = 90; const barWpx = 110; const barMax = 100;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 0' }}>
      {players.map(({ name, pct, status, blur }) => {
        const color = STATUS_COLORS[status] || STATUS_COLORS.none;
        const barW = Math.round((Math.abs(pct) / maxAbs) * barMax);
        const isOver = status === 'above';
        const showBar = status !== 'none';
        const pctLabel = status === 'none' ? '—' : `${pct > 0 ? '+' : ''}${pct}%`;
        return (
          <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, height: 28 }}>
            <div style={{ width: nameW, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', filter: blur ? 'blur(5px)' : 'none', userSelect: blur ? 'none' : 'auto' }}>{name}</div>
            <div style={{ width: barWpx, display: 'flex', justifyContent: 'flex-end' }}>
              {showBar && !isOver && <div style={{ width: `${barW}%`, maxWidth: barWpx, height: 18, background: color, borderRadius: '3px 0 0 3px', minWidth: 2 }} />}
            </div>
            <div style={{ width: 2, height: 20, background: 'var(--border-strong)', flexShrink: 0, marginLeft: 6, marginRight: 6 }} />
            <div style={{ width: barWpx, display: 'flex', justifyContent: 'flex-start' }}>
              {showBar && isOver && <div style={{ width: `${barW}%`, maxWidth: barWpx, height: 18, background: color, borderRadius: '0 3px 3px 0', minWidth: 2 }} />}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color, minWidth: 40, textAlign: 'right' }}>{pctLabel}</div>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 16, marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 11, color: 'var(--text-muted)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, background: STATUS_COLORS.below, borderRadius: 2, display: 'inline-block' }} /> Below lower target</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, background: STATUS_COLORS.amber, borderRadius: 2, display: 'inline-block' }} /> Near lower target</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, background: STATUS_COLORS.above, borderRadius: 2, display: 'inline-block' }} /> Above upper target</span>
        <span style={{ marginLeft: 'auto' }}>vs. lower/upper HMLD target</span>
      </div>
    </div>
  );
}

function PlayerRow({ player, isOdd, blurName = false }) {
  const { name, position, mdLabel, metrics, targets } = player;
  // Estimated rows (training estimates and, since 2026-10-24, KNVB match estimates): the shared
  // marker on the name, and per value only for the metrics that were estimated — "niet geschat"
  // for a metric a KNVB estimate left empty instead of a dash that reads like a measured 0.
  const estimate = player.is_estimated
    ? { ...(parseBasis(player.estimation_basis) || {}), method: parseBasis(player.estimation_basis)?.method || player.estimation_method }
    : null;
  const cell = (metricKey, targetKey) => {
    const val = metrics[metricKey] ?? 0;
    const tgt = targets?.[targetKey];
    const status = getStatus(val, tgt);
    const color  = STATUS_COLORS[status];
    return (
      <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color, fontWeight: 500, whiteSpace: 'nowrap' }}>
        {estimate ? <EstimatedValue value={metrics[metricKey] ?? null} basis={estimate} metric={metricKey} /> : (val ? Math.round(val).toLocaleString() : '—')}
        {tgt && tgt.upper > 0 && (
          <div style={{ fontSize: 10, color: 'var(--text-dimmed)', fontWeight: 400 }}>{Math.round(tgt.lower).toLocaleString()}–{Math.round(tgt.upper).toLocaleString()}</div>
        )}
      </td>
    );
  };
  const hmlStatus = getStatus(metrics.hmld ?? 0, targets?.HMLD);
  return (
    <tr style={{ background: isOdd ? 'var(--bg-row-alt)' : 'transparent', borderTop: '1px solid var(--border)' }}>
      <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[hmlStatus], flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', filter: blurName ? 'blur(5px)' : 'none', userSelect: blurName ? 'none' : 'auto' }}>{name}</span>
          {estimate && <EstimatedMarker basis={estimate} />}
        </div>
      </td>
      <td style={{ padding: '9px 12px', fontSize: 11, color: 'var(--text-muted)' }}>{position || '—'}</td>
      <td style={{ padding: '9px 12px' }}>
        {mdLabel ? <span style={{ fontSize: 11, color: '#a5b4fc', fontWeight: 600, background: '#312e81', padding: '2px 7px', borderRadius: 99 }}>{mdLabel}</span>
                 : <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>—</span>}
      </td>
      {cell('td','TD')} {cell('rd','RD')} {cell('hid','HID')} {cell('sd','SD')} {cell('sa','SA')} {cell('expl','Expl')} {cell('hmld','HMLD')}
      <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>{estimate ? <EstimatedValue value={metrics.max_speed ?? null} basis={estimate} metric="max_speed" format={v => `${Number(v).toFixed(1)} km/h`} /> : (metrics.max_speed ? `${metrics.max_speed} km/h` : '—')}</td>
      <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>{metrics.minutes_played ? `${Math.round(metrics.minutes_played)}'` : '—'}</td>
    </tr>
  );
}

export default function GPSData({ playerRecord = null, user = null, mode = 'team', initialSessionId = null, onNavigate = null }) {
  const { selectedSeasons } = useSeason();
  const selfPlayerId = playerRecord?.id ? String(playerRecord.id) : null;
  const [sessions, setSessions] = useState([]);
  const [players,  setPlayers]  = useState([]);
  const [rpeData,  setRpeData]  = useState([]);
  const [selected, setSelected] = useUrlParam('session', '');
  const [selectedPosition, setSelectedPosition] = useUrlParam('position', 'All');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [sortKey, setSortKey] = useState('HMLD');
  const [sortDir, setSortDir] = useState('desc');
  const [rpeSortKey, setRpeSortKey] = useState('rpe_muscular');
  const [rpeSortDir, setRpeSortDir] = useState('desc');
  const [loading,  setLoading]  = useState(true);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingNote, setEditingNote] = useState(false);
  const [estimateModalOpen, setEstimateModalOpen] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, s, rpe] = await Promise.all([getPlayers(), getGPS(), getRPE()]);
        setRpeData(rpe || []);
        setPlayers(p);
        const normalized = s.map(sess => ({
          ...sess,
          date:  sess.session_date?.slice(0, 10),
          title: sess.session_title,
          players: sess.players.map(pl => ({ ...pl, mdLabel: pl.md_label, playerId: pl.player_id })),
        }));
        setSessions(normalized);
        if (normalized.length) {
          // Deep-linked from elsewhere (e.g. tapping a session in the player Today timeline, or
          // a ?session=<id> URL param present at load) — land on that exact session instead of
          // always defaulting to the latest one. `selected` here is whatever the URL already
          // supplied on first render (captured once, since this effect only runs on mount).
          const requestedId = initialSessionId || selected || null;
          const requested = requestedId && normalized.find(s => String(s.id) === String(requestedId));
          const target = requested || [...normalized].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
          setSelected(String(target.id));
        }
      } catch (err) { console.error('Failed to load GPS:', err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const filteredSessions = useMemo(
    () => sessions.filter(s => {
      if (!s.date) return true;
      const season = dateToSeason(s.date);
      if (season && selectedSeasons.length > 0 && !selectedSeasons.includes(season)) return false;
      // A rehab-only day has no team players, and a normal team day has no
      // rehab players — either way, a session with nothing relevant to the
      // current mode shouldn't clutter the session dropdown.
      const relevant = mode === 'rehab' ? p => p.is_rehab : p => !p.is_rehab;
      return (s.players || []).some(relevant);
    }),
    [sessions, selectedSeasons, mode],
  );

  // Reset selected session when season filter changes
  useEffect(() => {
    if (!filteredSessions.length) { setSelected(''); return; }
    if (!filteredSessions.find(s => String(s.id) === selected)) {
      const latest = [...filteredSessions].sort((a, b) => b.date.localeCompare(a.date))[0];
      setSelected(String(latest.id));
    }
  }, [filteredSessions]);

  const session = filteredSessions.find(s => String(s.id) === selected);

  useEffect(() => {
    setNoteDraft(session?.notes || '');
    setEditingNote(false);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNote = async () => {
    if (!session) return;
    setSavingNote(true);
    try {
      const updated = await updateSessionNotes(session.id, noteDraft);
      setSessions(prev => prev.map(s => s.id === session.id ? { ...s, notes: updated.notes } : s));
      setEditingNote(false);
    } catch (err) {
      console.error('Failed to save session note:', err);
      alert('Failed to save note.');
    } finally {
      setSavingNote(false);
    }
  };

  // Individualized target resolution — batched through /api/targets/resolve-batch
  // (targetResolution.js, backend) rather than computed here, so this page gets the same
  // per-player intensity/day_intensity/target_overrides/MD-scaling precedence every other
  // training-load page now shares, instead of its own (previously non-deterministic:
  // `sc.playerId === 'team' || ...` picked whichever row the array happened to list first, not
  // "prefer the individual row") copy of the logic. Effect+state instead of a synchronous
  // useMemo since resolution is now a network call; keyed by player so a still-in-flight refetch
  // doesn't flash existing rows to empty.
  const [resolvedByKey, setResolvedByKey] = useState(new Map());
  const relevantPlayers = useMemo(() => {
    if (!session) return [];
    return mode === 'rehab' ? session.players.filter(p => p.is_rehab) : session.players.filter(p => !p.is_rehab);
  }, [session, mode]);

  useEffect(() => {
    if (!relevantPlayers.length || !session) { setResolvedByKey(new Map()); return; }
    const items = relevantPlayers.map(p => {
      const db = players.find(d => String(d.id) === String(p.player_id) || (d.name && p.player_name && d.name.toLowerCase() === p.player_name.toLowerCase()));
      const playerId = p.player_id || db?.id;
      return {
        key: String(playerId ?? p.player_name), playerId, position: db?.position || p.position || null,
        scenario: session.scenario, date: session.date, dayLabel: p.md_label || p.mdLabel,
      };
    });
    let cancelled = false;
    resolveTargetsBatch(items)
      .then(({ results }) => { if (!cancelled) setResolvedByKey(new Map(results.map(r => [r.key, r]))); })
      .catch(err => { console.error('Failed to resolve targets:', err); if (!cancelled) setResolvedByKey(new Map()); });
    return () => { cancelled = true; };
  }, [relevantPlayers, session, players]);

  const enriched = useMemo(() => {
    return relevantPlayers.map(p => {
      const db = players.find(d => String(d.id) === String(p.player_id) || (d.name && p.player_name && d.name.toLowerCase() === p.player_name.toLowerCase()));
      const pos = db?.position || p.position || null;
      const mdLabel = p.md_label || p.mdLabel;
      const key = String((p.player_id || db?.id) ?? p.player_name);
      const targets = resolvedByKey.get(key)?.targets || null;
      return { ...p, name: p.player_name || p.name || 'Unknown', position: pos, mdLabel, targets,
        metrics: { td: p.td??0, rd: p.rd??0, hid: p.hid??0, sd: p.sd??0, sa: p.sa??0, expl: p.expl??0, hmld: p.hmld??0, HMLD: p.hmld??0, max_speed: p.max_speed??null, minutes_played: p.minutes_played??null } };
    });
  }, [relevantPlayers, players, resolvedByKey]);

  const positions = useMemo(() => {
    const set = new Set(enriched.map(p => p.position).filter(Boolean));
    return ['All', ...Array.from(set)];
  }, [enriched]);

  const filteredPlayers = useMemo(() => {
    const byPos = selectedPosition && selectedPosition !== 'All' ? enriched.filter(p => p.position === selectedPosition) : [...enriched];
    if (selectedStatus === 'all') return byPos;
    return byPos.filter(p => {
      const s = getStatus(p.metrics.hmld, p.targets?.HMLD);
      if (selectedStatus === 'reached') return s === 'green';
      if (selectedStatus === 'near') return s === 'amber';
      if (selectedStatus === 'below') return s === 'below';
      if (selectedStatus === 'above') return s === 'above';
      if (selectedStatus === 'failed') return s === 'above' || s === 'below';
      return true;
    });
  }, [enriched, selectedPosition, selectedStatus]);

  // Who's currently flagged injured/ill/excluded in Medical on this exact session date — same
  // /api/availability computeAvailabilityDay status GPS & Load's Week Summary already gates its
  // own squad average on (gpsWeekSummary.js's isOnSquadPlan). A player can still show up here
  // with a real recorded session (he trained despite the flag), but he shouldn't count toward
  // "the squad's" average load for the day, same reasoning as Week Summary's fix.
  const [unavailablePlayerIds, setUnavailablePlayerIds] = useState(new Set());
  useEffect(() => {
    if (!session?.date) { setUnavailablePlayerIds(new Set()); return; }
    let cancelled = false;
    getAvailability(session.date, session.date)
      .then(data => {
        if (cancelled) return;
        const ids = new Set(
          (data?.players || [])
            .filter(p => p.days?.[0] && p.days[0].status !== 'available')
            .map(p => String(p.player_id))
        );
        setUnavailablePlayerIds(ids);
      })
      .catch(err => { console.error('Failed to load availability:', err); if (!cancelled) setUnavailablePlayerIds(new Set()); });
    return () => { cancelled = true; };
  }, [session?.date]);

  // Squad-average population: filteredPlayers minus anyone currently unavailable per Medical.
  // The player table below still lists everyone who trained (including a flagged player's real
  // numbers) — only the squad-wide averages/targets exclude them.
  const squadPlayers = useMemo(
    () => filteredPlayers.filter(p => !unavailablePlayerIds.has(String(p.player_id))),
    [filteredPlayers, unavailablePlayerIds],
  );

  const teamTotals = useMemo(() => ({
    // compute averages across squadPlayers (per-player average, excluding unavailable players)
    TD: squadPlayers.length ? squadPlayers.reduce((s,p)=>s+(p.metrics.td||0),0)/squadPlayers.length : 0,
    RD: squadPlayers.length ? squadPlayers.reduce((s,p)=>s+(p.metrics.rd||0),0)/squadPlayers.length : 0,
    HID: squadPlayers.length ? squadPlayers.reduce((s,p)=>s+(p.metrics.hid||0),0)/squadPlayers.length : 0,
    SD: squadPlayers.length ? squadPlayers.reduce((s,p)=>s+(p.metrics.sd||0),0)/squadPlayers.length : 0,
    SA: squadPlayers.length ? squadPlayers.reduce((s,p)=>s+(p.metrics.sa||0),0)/squadPlayers.length : 0,
    Expl: squadPlayers.length ? squadPlayers.reduce((s,p)=>s+(p.metrics.expl||0),0)/squadPlayers.length : 0,
    HMLD: squadPlayers.length ? squadPlayers.reduce((s,p)=>s+(p.metrics.hmld||0),0)/squadPlayers.length : 0,
  }), [squadPlayers]);

  const teamTargets = useMemo(() => {
    const keys = ['TD','RD','HID','SD','SA','Expl','HMLD'];
    const result = {};
    keys.forEach(k => {
      const lowers = squadPlayers.filter(p=>p.targets?.[k]).map(p=>p.targets[k].lower);
      const uppers = squadPlayers.filter(p=>p.targets?.[k]).map(p=>p.targets[k].upper);
      result[k] = lowers.length ? { lower: lowers.reduce((a,b)=>a+b,0)/lowers.length, upper: uppers.reduce((a,b)=>a+b,0)/uppers.length } : null;
    });
    return result;
  }, [squadPlayers]);

  const counts = useMemo(() => ({
    green: filteredPlayers.filter(p=>getStatus(p.metrics.hmld,p.targets?.HMLD)==='green').length,
    amber: filteredPlayers.filter(p=>getStatus(p.metrics.hmld,p.targets?.HMLD)==='amber').length,
    below: filteredPlayers.filter(p=>getStatus(p.metrics.hmld,p.targets?.HMLD)==='below').length,
    above: filteredPlayers.filter(p=>getStatus(p.metrics.hmld,p.targets?.HMLD)==='above').length,
  }), [filteredPlayers]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', color:'var(--text-muted)', gap:12 }}>
      <Activity size={24} strokeWidth={1.5} /><span style={{ fontSize:15 }}>Loading session data…</span>
    </div>
  );
  if (!sessions.length) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60vh', gap:16, opacity:0.5 }}>
      <Activity size={64} strokeWidth={1} />
      <h2 style={{ fontSize:20, fontWeight:600 }}>No GPS sessions yet</h2>
      <p style={{ fontSize:14, color:'var(--text-muted)' }}>Upload a Statsports CSV in the Upload zone first.</p>
    </div>
  );
  if (!filteredSessions.length) return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'60vh', gap:16, opacity:0.5 }}>
      <Activity size={64} strokeWidth={1} />
      <h2 style={{ fontSize:20, fontWeight:600 }}>{mode === 'rehab' ? 'No rehab sessions yet' : 'No data for this season'}</h2>
      <p style={{ fontSize:14, color:'var(--text-muted)' }}>{mode === 'rehab' ? 'Sessions with a "Rehab-Full session" drill will show up here.' : 'Change the season filter in Settings to view other data.'}</p>
    </div>
  );

  const thStyle = { padding:'10px 12px', textAlign:'right', fontSize:11, fontWeight:700, color:'var(--text-muted)', whiteSpace:'nowrap', background:'var(--bg-app)' };

  return (
    <div style={{ maxWidth:1400, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
            <h1 style={{ fontSize:22, fontWeight:700 }}>{mode === 'rehab' ? 'Rehab Session Report' : 'Session Report'}</h1>
            {mode === 'team' && onNavigate && (user?.isAdmin || (user?.allowedTabs || []).includes('metrics-table')) && (
              <button onClick={() => onNavigate('metrics-table')}
                style={{ padding:'6px 12px', borderRadius:8, border:'1px solid var(--border-strong)', background:'var(--bg-app)', color:'var(--text-secondary)', fontSize:12.5, fontWeight:600, cursor:'pointer' }}>
                Advanced metrics table
              </button>
            )}
          </div>
          <p style={{ color:'var(--text-muted)', fontSize:14, marginTop:4 }}>{mode === 'rehab' ? 'Individual rehab sessions with the physio, load overview with target comparisons.' : 'Team load overview with target comparisons.'}</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <select value={selected} onChange={e=>setSelected(e.target.value)}
            style={{ padding:'10px 16px', borderRadius:8, border:'1px solid var(--border-strong)', background:'var(--border)', color:'var(--text-primary)', fontSize:14, outline:'none', cursor:'pointer' }}>
            {[...filteredSessions].sort((a,b)=>new Date(b.date)-new Date(a.date)).map(s=>(
              <option key={s.id} value={s.id}>{s.date} — {s.title||'Session'}</option>
            ))}
          </select>
          <select value={selectedPosition} onChange={e=>setSelectedPosition(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1px solid var(--border-strong)', background:'var(--bg-app)', color:'var(--text-primary)', fontSize:13, outline:'none', cursor:'pointer' }}>
            {positions.map(pos => (<option key={pos} value={pos}>{pos}</option>))}
          </select>
          <select value={selectedStatus} onChange={e=>setSelectedStatus(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1px solid var(--border-strong)', background:'var(--bg-app)', color:'var(--text-primary)', fontSize:13, outline:'none', cursor:'pointer' }}>
            <option value="all">All statuses</option>
            <option value="reached">On target</option>
            <option value="near">Near target</option>
            <option value="below">Under target</option>
            <option value="above">Over target</option>
            <option value="failed">Off target</option>
          </select>
        </div>
      </div>

      {session && (<>
        <div style={{ display:'flex', gap:12, marginBottom:24, flexWrap:'wrap', alignItems:'stretch' }}>
          <div style={{ background:'var(--border)', borderRadius:12, padding:'16px 20px', border:'1px solid var(--border-strong)', flex:1, minWidth:220 }}>
            <div style={{ fontSize:18, fontWeight:700 }}>{session.title||'Session'}</div>
            <div style={{ fontSize:13, color:'var(--text-muted)', marginTop:2 }}>{session.date} · {enriched.length} players</div>
            {enriched[0]?.mdLabel && (
              <div style={{ marginTop:8, display:'inline-block', padding:'3px 10px', borderRadius:99, background:'#312e81', color:'#a5b4fc', fontSize:12, fontWeight:600 }}>
                {enriched[0].mdLabel} · Scenario {session.scenario}
              </div>
            )}
          </div>
          {[
            {label:'On target',    val:counts.green, key:'reached', color:'#10b981', rgba:'16,185,129'},
            {label:'Near target',  val:counts.amber, key:'near',    color:'#f59e0b', rgba:'245,158,11'},
            {label:'Under target', val:counts.below, key:'below',   color:'#ef4444', rgba:'239,68,68'},
            {label:'Over target',  val:counts.above, key:'above',   color:'#7c3aed', rgba:'124,58,237'},
          ].map(({label,val,key,color,rgba})=> {
            const active = selectedStatus === key;
            return (
              <div key={label}
                onClick={() => setSelectedStatus(active ? 'all' : key)}
                style={{
                  background: active ? `rgba(${rgba},0.15)` : `rgba(${rgba},0.07)`,
                  borderRadius:12,
                  padding:'16px 20px',
                  border:`1px solid rgba(${rgba},${active ? '0.5' : '0.2'})`,
                  textAlign:'center',
                  minWidth:100,
                  display:'flex',
                  flexDirection:'column',
                  justifyContent:'center',
                  cursor:'pointer',
                  transition:'all 0.15s',
                }}>
                <div style={{ fontSize:30, fontWeight:800, color }}>{val}</div>
                <div style={{ fontSize:12, color, opacity:0.75, marginTop:2 }}>{label}</div>
              </div>
            );
          })}
        </div>

        {!playerRecord && session && ['staff', 'admin'].includes(user?.role) && (
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}>
            <button onClick={() => setEstimateModalOpen(true)} style={{ padding:'8px 14px', borderRadius:8, border:'1px solid var(--border-strong)', background:'none', color:'var(--text-primary)', cursor:'pointer', fontSize:13, fontWeight:600 }}>
              Estimate missed GPS data
            </button>
          </div>
        )}
        {estimateModalOpen && session && (
          <EstimateModal
            sessionId={session.id}
            user={user}
            onClose={() => setEstimateModalOpen(false)}
            onCommitted={() => getGPS().then(setSessions)}
          />
        )}

        {!playerRecord && (
          <div style={{ background:'var(--border)', borderRadius:12, padding:'16px 20px', border:'1px solid var(--border-strong)', marginBottom:24 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: (editingNote || noteDraft) ? 10 : 0 }}>
              <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', display:'flex', alignItems:'center', gap:6 }}>
                <FileText size={14} /> Trainer's notes
              </div>
              {user?.isAdmin && !editingNote && (
                <button onClick={() => setEditingNote(true)} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', display:'flex', alignItems:'center', gap:4, fontSize:12, padding:0 }}>
                  <Pencil size={12} /> {noteDraft ? 'Edit' : 'Add note'}
                </button>
              )}
            </div>
            {editingNote ? (
              <div>
                <textarea
                  value={noteDraft}
                  onChange={e => setNoteDraft(e.target.value)}
                  placeholder="Write a review of this training session for the staff to see…"
                  style={{ width:'100%', minHeight:90, padding:'10px 12px', borderRadius:8, border:'1px solid var(--border-strong)', background:'var(--bg-app)', color:'var(--text-primary)', fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }}
                />
                <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:8 }}>
                  <button onClick={() => { setNoteDraft(session.notes || ''); setEditingNote(false); }} style={{ padding:'6px 12px', borderRadius:8, border:'1px solid var(--border-strong)', background:'none', color:'var(--text-secondary)', cursor:'pointer', fontSize:12 }}>Cancel</button>
                  <button onClick={saveNote} disabled={savingNote} style={{ padding:'6px 12px', borderRadius:8, border:'none', background:'#3b82f6', color:'#fff', cursor:'pointer', fontSize:12, fontWeight:600 }}>{savingNote ? 'Saving…' : 'Save note'}</button>
                </div>
              </div>
            ) : noteDraft ? (
              <div style={{ fontSize:13, color:'var(--text-primary)', whiteSpace:'pre-wrap', lineHeight:1.5 }}>{noteDraft}</div>
            ) : (
              <div style={{ fontSize:13, color:'var(--text-dimmed)', fontStyle:'italic' }}>No notes for this session yet.</div>
            )}
          </div>
        )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 420px', gap:16, marginBottom:24, alignItems:'start' }}>
          <div style={{ background:'var(--border)', borderRadius:12, padding:'20px 16px', border:'1px solid var(--border-strong)' }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:16 }}>
              <Users size={14} style={{ verticalAlign:'middle', marginRight:6 }} />Team totals — entire session
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8, justifyContent:'space-around' }}>
              {GAUGE_METRICS.map(m=><Gauge key={m.key} label={m.label} value={teamTotals[m.key]} targets={teamTargets[m.key]} unit={m.unit} category={m.category} />)}
            </div>
            <div style={{ marginTop:20, borderTop:'1px solid var(--border-strong)', paddingTop:16, display:'flex', alignItems:'center', gap:16 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginBottom:4 }}>Total team HMLD</div>
                <div style={{ fontSize:26, fontWeight:800, color:STATUS_COLORS[getStatus(teamTotals.HMLD,teamTargets.HMLD)] }}>
                  {Math.round(teamTotals.HMLD).toLocaleString()} <span style={{ fontSize:14, fontWeight:400, color:'var(--text-muted)' }}>m</span>
                </div>
                {teamTargets.HMLD && <div style={{ fontSize:11, color:'var(--text-dimmed)', marginTop:2 }}>target {Math.round(teamTargets.HMLD.lower).toLocaleString()}–{Math.round(teamTargets.HMLD.upper).toLocaleString()} m</div>}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                {[
                  {label:'Avg HMLD / player', val:squadPlayers.length?Math.round(teamTotals.HMLD).toLocaleString():'—', unit:'m'},
                  {label:'Avg max speed', val:filteredPlayers.filter(p=>p.metrics.max_speed).length?(filteredPlayers.reduce((s,p)=>s+(p.metrics.max_speed||0),0)/filteredPlayers.filter(p=>p.metrics.max_speed).length).toFixed(1):'—', unit:'km/h'},
                  {label:'Players tracked', val:filteredPlayers.length, unit:''},
                ].map(({label,val,unit})=>(
                  <div key={label} style={{ background:'var(--bg-app)', borderRadius:8, padding:'10px 14px', textAlign:'center' }}>
                    <div style={{ fontSize:10, color:'var(--text-muted)', marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:16, fontWeight:700, color:'var(--text-primary)' }}>{val}<span style={{ fontSize:11, color:'var(--text-muted)' }}>{unit?` ${unit}`:''}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ background:'var(--border)', borderRadius:12, padding:'20px 16px', border:'1px solid var(--border-strong)' }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text-secondary)', marginBottom:16 }}>
              <TrendingUp size={14} style={{ verticalAlign:'middle', marginRight:6 }} />Over / Underloaded players
            </div>
            <LoadChart enriched={filteredPlayers} selfPlayerId={selfPlayerId} />
          </div>
        </div>

        <div style={{ background:'var(--border)', borderRadius:12, border:'1px solid var(--border-strong)', overflow:'hidden' }}>
          <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border-strong)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span style={{ fontWeight:700, fontSize:15 }}>Individual breakdown</span>
            <span style={{ fontSize:12, color:'var(--text-dimmed)' }}>Color = vs. HMLD target · Range shown beneath value</span>
          </div>
            <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign:'left', cursor:'pointer' }} onClick={()=>{ if(sortKey==='name') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('name'); setSortDir('asc'); } }}>Player {sortKey==='name'?(sortDir==='asc'?'▲':'▼'):''}</th>
                  <th style={{ ...thStyle, textAlign:'left', cursor:'pointer' }} onClick={()=>{ if(sortKey==='position') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('position'); setSortDir('asc'); } }}>Pos {sortKey==='position'?(sortDir==='asc'?'▲':'▼'):''}</th>
                  <th style={{ ...thStyle, textAlign:'left', cursor:'pointer' }} onClick={()=>{ if(sortKey==='mdLabel') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('mdLabel'); setSortDir('asc'); } }}>MD {sortKey==='mdLabel'?(sortDir==='asc'?'▲':'▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='TD') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('TD'); setSortDir('desc'); } }}>TD (m){sortKey==='TD'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='RD') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('RD'); setSortDir('desc'); } }}>RD &gt;14.4{sortKey==='RD'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='HID') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('HID'); setSortDir('desc'); } }}>HID &gt;19.8{sortKey==='HID'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='SD') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('SD'); setSortDir('desc'); } }}>Sprint &gt;25.2{sortKey==='SD'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='SA') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('SA'); setSortDir('desc'); } }}>Short Act.{sortKey==='SA'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='Expl') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('Expl'); setSortDir('desc'); } }}>Expl. Dist.{sortKey==='Expl'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='HMLD') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('HMLD'); setSortDir('desc'); } }}>HMLD{sortKey==='HMLD'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='max_speed') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('max_speed'); setSortDir('desc'); } }}>Max Spd{sortKey==='max_speed'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                  <th style={{...thStyle,cursor:'pointer'}} onClick={()=>{ if(sortKey==='minutes_played') setSortDir(sortDir==='desc'?'asc':'desc'); else { setSortKey('minutes_played'); setSortDir('desc'); } }}>Mins{sortKey==='minutes_played'?(sortDir==='asc'?' ▲':' ▼'):''}</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const list = [...filteredPlayers];
                  const k = sortKey;
                  const dir = sortDir === 'asc' ? 1 : -1;
                  list.sort((a,b)=>{
                    const get = (item,key)=>{
                      if (!item) return null;
                      if (key==='name' || key==='position' || key==='mdLabel') return String(item[key]||'').toLowerCase();
                      return (item.metrics && (item.metrics[key.toLowerCase()] ?? item.metrics[key] ?? item[key])) ?? 0;
                    };
                    const va = get(a,k); const vb = get(b,k);
                    if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * dir;
                    return (Number(va) - Number(vb)) * dir;
                  });
                  return list.map((p,i)=>{
                    const blurName = !!selfPlayerId && String(p.player_id) !== selfPlayerId && p.name?.toLowerCase() !== playerRecord?.name?.toLowerCase();
                    return <PlayerRow key={p.player_id||p.name} player={p} isOdd={i%2!==0} blurName={blurName} />;
                  });
                })()}
              </tbody>
              <tfoot>
                <tr style={{ background:'var(--bg-app)', borderTop:'2px solid var(--border-strong)' }}>
                  <td colSpan={3} style={{ padding:'10px 12px', fontSize:12, fontWeight:700, color:'var(--text-secondary)' }}>Team total</td>
                  {[teamTotals.TD,teamTotals.RD,teamTotals.HID,teamTotals.SD,teamTotals.SA,teamTotals.Expl,teamTotals.HMLD].map((val,i)=>(
                    <td key={i} style={{ padding:'10px 12px', textAlign:'right', fontSize:13, fontWeight:700, color:'var(--text-primary)' }}>{val?Math.round(val).toLocaleString():'—'}</td>
                  ))}
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      {/* ── Session RPE ── */}
      {(() => {
        if (!session) return null;
        const sessionDate = session.date;
        const sessionRpe = rpeData.filter(r => (r.date || '').slice(0, 10) === sessionDate);
        const activePlayers = players.filter(p => p.active !== false);

        // Match RPE entry to player (by player_id or name)
        const rpeForPlayer = (p) => sessionRpe.find(r =>
          String(r.player_id) === String(p.id) ||
          (r.player_name && p.name && r.player_name.toLowerCase() === p.name.toLowerCase())
        );

        const submitted = activePlayers.filter(p => rpeForPlayer(p));

        const avgM = submitted.length ? submitted.reduce((s, p) => s + Number(rpeForPlayer(p)?.rpe_muscular || 0), 0) / submitted.length : null;
        const avgC = submitted.length ? submitted.reduce((s, p) => s + Number(rpeForPlayer(p)?.rpe_cardiovascular || 0), 0) / submitted.length : null;

        const rpeColor = v => v == null ? 'var(--text-dimmed)' : rpeValueColor(v);
        const rpeBarColor = rpeColor;

        const handleRpeSort = (key) => {
          if (rpeSortKey === key) setRpeSortDir(d => d === 'asc' ? 'desc' : 'asc');
          else { setRpeSortKey(key); setRpeSortDir(key === 'name' ? 'asc' : 'desc'); }
        };
        const rpeSortIndicator = key => rpeSortKey === key ? (rpeSortDir === 'asc' ? ' ▲' : ' ▼') : '';

        // Every active player is listed, including those who haven't
        // submitted yet (rpe: null), so gaps are visible at a glance.
        const rows = [...activePlayers]
          .map(p => ({ player: p, rpe: rpeForPlayer(p) }))
          .sort((a, b) => {
            const dir = rpeSortDir === 'asc' ? 1 : -1;
            if (rpeSortKey === 'name') return (a.player.name || '').localeCompare(b.player.name || '') * dir;
            const av = a.rpe ? Number(a.rpe[rpeSortKey]) : null;
            const bv = b.rpe ? Number(b.rpe[rpeSortKey]) : null;
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return (av - bv) * dir;
          });

        return (
          <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14, marginTop:20, overflow:'hidden' }}>
            {/* Header + summary stats */}
            <div style={{ padding:'13px 18px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
              <span style={{ fontSize:'0.86rem', fontWeight:700, color:'var(--text-primary)' }}>Session RPE</span>
              <div style={{ display:'flex', gap:24, alignItems:'center', flexWrap:'wrap' }}>
                {avgM != null && (
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'0.65rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:2 }}>Avg Muscular</div>
                    <div style={{ fontSize:'1.1rem', fontWeight:700, color:rpeColor(Math.round(avgM)) }}>{avgM.toFixed(1)}</div>
                    <div style={{ fontSize:'0.7rem', color:rpeColor(Math.round(avgM)), marginTop:2 }}>{rpeAssessment(Math.round(avgM))}</div>
                  </div>
                )}
                {avgC != null && (
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:'0.65rem', fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:2 }}>Avg Cardiovascular</div>
                    <div style={{ fontSize:'1.1rem', fontWeight:700, color:rpeColor(Math.round(avgC)) }}>{avgC.toFixed(1)}</div>
                    <div style={{ fontSize:'0.7rem', color:rpeColor(Math.round(avgC)), marginTop:2 }}>{rpeAssessment(Math.round(avgC))}</div>
                  </div>
                )}
                <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', borderLeft:'1px solid var(--border)', paddingLeft:16 }}>
                  <span style={{ fontWeight:600, color:'var(--text-primary)' }}>{submitted.length}</span> of {activePlayers.length} players submitted
                </div>
              </div>
            </div>
            {/* Per-player rows */}
            {rows.length === 0 && (
              <div style={{ padding:'20px 18px', color:'var(--text-dimmed)', fontSize:'0.83rem', fontStyle:'italic' }}>
                No active players found.
              </div>
            )}
            {rows.length > 0 && (
              <div style={{ padding:'8px 18px 14px' }}>
                {/* Column headers */}
                <div style={{ display:'grid', gridTemplateColumns:'180px 1fr 1fr', gap:16, padding:'0 0 6px', marginBottom:2, borderBottom:'1px solid var(--border)' }}>
                  <span style={{ fontSize:'0.65rem', fontWeight:600, color:'var(--text-dimmed)', textTransform:'uppercase', letterSpacing:'0.07em', cursor:'pointer', userSelect:'none' }} onClick={() => handleRpeSort('name')}>Player{rpeSortIndicator('name')}</span>
                  <span style={{ fontSize:'0.65rem', fontWeight:600, color:'var(--text-dimmed)', textTransform:'uppercase', letterSpacing:'0.07em', cursor:'pointer', userSelect:'none' }} onClick={() => handleRpeSort('rpe_muscular')}>Muscular RPE{rpeSortIndicator('rpe_muscular')}</span>
                  <span style={{ fontSize:'0.65rem', fontWeight:600, color:'var(--text-dimmed)', textTransform:'uppercase', letterSpacing:'0.07em', cursor:'pointer', userSelect:'none' }} onClick={() => handleRpeSort('rpe_cardiovascular')}>Cardiovascular RPE{rpeSortIndicator('rpe_cardiovascular')}</span>
                </div>
                {rows.map(({ player, rpe }, i) => {
                  const m = Number(rpe?.rpe_muscular) || null;
                  const c = Number(rpe?.rpe_cardiovascular) || null;
                  return (
                    <div key={player.id} style={{ display:'grid', gridTemplateColumns:'180px 1fr 1fr', gap:16, alignItems:'center', padding:'7px 0', borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ fontSize:'0.82rem', fontWeight:500, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', filter: (selfPlayerId && String(player.id) !== selfPlayerId) ? 'blur(5px)' : 'none', userSelect: (selfPlayerId && String(player.id) !== selfPlayerId) ? 'none' : 'auto' }}>{player.name}</div>
                      {/* Muscular bar */}
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ flex:1, height:8, borderRadius:99, background:'var(--bg-app)', overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${(m / 10) * 100}%`, background:rpeBarColor(m), borderRadius:99, transition:'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize:'0.78rem', fontWeight:700, color:rpeColor(m), minWidth:18, textAlign:'right' }}>{m ?? '—'}</span>
                      </div>
                      {/* Cardiovascular bar */}
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ flex:1, height:8, borderRadius:99, background:'var(--bg-app)', overflow:'hidden' }}>
                          <div style={{ height:'100%', width:`${(c / 10) * 100}%`, background:rpeBarColor(c), borderRadius:99, transition:'width 0.3s' }} />
                        </div>
                        <span style={{ fontSize:'0.78rem', fontWeight:700, color:rpeColor(c), minWidth:18, textAlign:'right' }}>{c ?? '—'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}
      </>)}
    </div>
  );
}
